import { useEffect, useId, useState } from 'react';

import {
  EXEC_GRANT_MAX_DURATION_LABEL,
  executionGrantExpiryPreview,
  parseExecutionGrantDuration,
  type ExecutionGrantDurationUnit
} from '../utils/executionGrantDuration';
import './ExecutionGrantDurationField.css';

type Props = {
  amount: string;
  unit: ExecutionGrantDurationUnit;
  onAmountChange: (value: string) => void;
  onUnitChange: (value: ExecutionGrantDurationUnit) => void;
  disabled?: boolean;
};

export function ExecutionGrantDurationField({
  amount, unit, onAmountChange, onUnitChange, disabled = false
}: Props) {
  const [now, setNow] = useState(() => new Date());
  const errorId = useId();
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const { minutes, error } = parseExecutionGrantDuration(amount, unit);
  return (
    <div className="stack">
      <div role="group" aria-label="Grant duration">
        <div className="status">Grant duration</div>
        <div className="execution-grant-duration__controls">
          <input
            type="number"
            min={0}
            step="any"
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
            aria-label="Grant duration amount"
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            aria-errormessage={error ? errorId : undefined}
            disabled={disabled}
          />
          <select
            value={unit}
            onChange={(event) => onUnitChange(event.target.value as ExecutionGrantDurationUnit)}
            aria-label="Grant duration unit"
            disabled={disabled}
          >
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
          </select>
        </div>
      </div>
      <div className="status">Maximum: {EXEC_GRANT_MAX_DURATION_LABEL}. Fractional hours or days must equal whole minutes.</div>
      {error ? (
        <div id={errorId} className="status error-inline" role="alert">{error}</div>
      ) : (
        <div className="status" aria-live="off">
          If created now, expires {executionGrantExpiryPreview(minutes!, now)}.
        </div>
      )}
    </div>
  );
}
